/* eslint-disable max-len */
import { TDBitToggle, TDocFlag, TOGGLE_KEY_PATTERN } from './constant';
import { getVersionFromUA, compareVersion, withDefaultUA, withDefaultUAAndVersion, getBitToggleSetting } from './util';
/** *******************************************************************************
 * 一、硬件级别判断
 *********************************************************************************/
/**
 * 是否是移动端, iPad 和 Android Pad 和 Harmony Pad 包含在移动端
 */
export var isMobile = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(mobile)/i.test(userAgent) || isAndroidPad(userAgent) || isHarmonyPad(userAgent);
});
/**
 * 是否是手机端
 */
export var isMobilePhone = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(mobile)/i.test(userAgent) && !isIPad(userAgent);
});
/**
 * 是否是 iPhone
 */
export var isIPhone = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(iphone)/i.test(userAgent);
});
/**
 * 是否是 iPad
 */
export var isIPad = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(ipad)/i.test(userAgent);
});
/**
 * 是否是 Android 手机
 */
export var isAndroidPhone = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(mobile)/i.test(userAgent) && isAndroid(userAgent);
});
/**
 * 是否是 Android 平板
 */
export var isAndroidPad = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return (!/(mobile)/i.test(userAgent) || /(tdocsIsPad)/i.test(userAgent)) && isAndroid(userAgent);
});
/**
 * 是否是 Harmony 手机
 */
export var isHarmonyPhone = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isHarmony(userAgent) && /mobile/i.test(userAgent);
});
/**
 * 是否是 Harmony 平板
 */
export var isHarmonyPad = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isHarmony(userAgent) && !/mobile/i.test(userAgent);
});
/**
 * 是否是 PC 或 Mac 设备
 */
export var isPC = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return !isMobile(userAgent);
});
/**
 * 是否是 Mac 电脑
 */
export var isMac = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return !isMobile(userAgent) && /Mac OS X/i.test(userAgent);
});
/**
 * 是否是 iPad 模拟的 Mac
 *
 * 背景：iPad OS 13 以上的 Safari 浏览器会默认模拟 Mac，UA 与 Mac 完全一致，这种情况前端可以通过触摸特性来判断，后端完全不可判断。
 * 为了防止判断为 iPad 导致与后端返回的 html 等资源产生平台不一致，我们仍然默认认为它是一个 Mac（`isMac` 为 `true`、`isIPad` 为
 * `false`），但同时也给出判断方法，用于前端微小逻辑的区分。
 *
 * Safari 浏览器的这个特性可以通过点击地址栏左侧的 “AA” 按钮 - “请求桌面网站” 来临时关闭，或者到 “网站设置” 中对当前域名长期关闭。
 *
 * 品类使用 DWS 插件 ipad-safari（https://git.woa.com/dws/dws-plugins-market/ipad-safari/）可以将这种情况改为下发 pad 端的制品，
 * 并能够自动修改 UserAgent 将其正确还原为 pad 端。
 */
export var isIPadEmulatedMac = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    var _a;
    // iPad 模拟的 Mac 的 maxTouchPoints > 1
    // Chrome 模拟的 iPad Air/Pro 没有这个行为，但是我们通过判断 vendor 来包含这种情况，方便开发和调试
    return isMac(userAgent) && isSafari(userAgent) && (navigator.maxTouchPoints > 1 || ((_a = navigator.vendor) === null || _a === void 0 ? void 0 : _a.startsWith('Google')));
});
/** *******************************************************************************
 * 二、操作系统级别判断
 *********************************************************************************/
/**
 * 是否是 Android 平台
 */
export var isAndroid = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /android|adr/i.test(userAgent);
});
/**
 * 获取 Android 版本
 */
export var getAndroidVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isAndroid(userAgent) ? getVersionFromUA('Android', userAgent) : null;
});
/**
 * 判断 Android 是否高于指定的版本
 */
export var isAndroidVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getAndroidVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 Harmony 平台
 */
export var isHarmony = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /OpenHarmony/i.test(userAgent);
});
/**
 * 获取 Harmony 版本
 */
export var getHarmonyVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isHarmony(userAgent) ? getVersionFromUA('OpenHarmony', userAgent) : null;
});
/**
 * 判断 Harmony 是否高于指定的版本
 */
export var isHarmonyVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getHarmonyVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 iOS 平台
 */
export var isIOS = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(iphone|ipad|ipod)/i.test(userAgent);
});
/**
 * 获取 iOS 版本
 */
export var getIOSVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isIOS(userAgent) ? getVersionFromUA('OS', userAgent) : null;
});
/**
 * 判断 IOS 是否高于指定的版本
 */
export var isIOSVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getIOSVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 Windows 平台
 */
export var isWindows = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(win64|wow64|win32|wow32|windows nt)/i.test(userAgent);
});
/**
 * 是否在 Windows Store 的 UWP 软件上运行
 */
export var isWindowsUWP = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /MSAppHost/i.test(userAgent);
});
/**
 * 是否是 Linux 系统（不含安卓）
 */
export var isLinux = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /linux/i.test(userAgent) && !isAndroid(userAgent);
});
/**
 * 是否是麒麟 Linux 系统
 */
export var isLinuxKylin = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /linux-kylin/i.test(userAgent);
});
/**
 * 是否是统信 Linux 系统
 */
export var isLinuxUOS = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /linux-uos/i.test(userAgent);
});
/** *******************************************************************************
 * 三、浏览器/浏览器内核级别判断
 *********************************************************************************/
/**
 * 是不是 PC 上的 QQ 浏览器
 * 会自动排除 PC QQ
 */
export var isPCQQBrowser = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return (!isMobile(userAgent)
        && /QQBrowser/i.test(userAgent)
        // 排除 PC QQ
        && !/QBCore/i.test(userAgent));
});
/**
 * 获取 PC QQ 浏览器版本
 */
export var getPCQQBrowserVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isPCQQBrowser(userAgent) ? getVersionFromUA('QQBrowser', userAgent) : null;
});
/**
 * 判断 PC QQ 浏览器是否高于指定的版本
 */
export var isPCQQBrowserVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getPCQQBrowserVersion(userAgent) || '', version) >= 0;
});
/**
 * 是不是移动端上的 QQ 浏览器
 * 会自动排除 QQ / TIM / 微信 / 企业微信 / 独立 App / 腾讯会议
 */
export var isMobileQQBrowser = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return (isMobile(userAgent)
        && /MQQBrowser/i.test(userAgent)
        && !isTimOrQQ(userAgent)
        && !isWeChat(userAgent)
        && !isWxWork(userAgent)
        && !isTencentDocsApp(userAgent)
        && !isWeiYunApp(userAgent)
        && !isMiniProgram(userAgent)
        && !isTencentMeeting(userAgent));
});
/**
 * 获取移动端 QQ 浏览器版本
 */
export var getMobileQQBrowserVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isMobileQQBrowser(userAgent) ? getVersionFromUA('MQQBrowser', userAgent) : null;
});
/**
 * 判断移动端 QQ 浏览器是否高于指定的版本
 */
export var isMobileQQBrowserVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getMobileQQBrowserVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 QQ 浏览器
 */
export var isQQBrowser = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isMobile(userAgent) ? isMobileQQBrowser(userAgent) : isPCQQBrowser(userAgent);
});
/**
 * 是否是 Harmony 浏览器
 * 会自动排除 QQ / TIM / 微信 / 企业微信 / 独立 App / 腾讯会议
 */
export var isHarmonyBrowser = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return (isHarmony(userAgent)
        && !isTimOrQQ(userAgent)
        && !isWeChat(userAgent)
        && !isWxWork(userAgent)
        && !isTencentDocsApp(userAgent)
        && !isWeiYunApp(userAgent)
        && !isMiniProgram(userAgent)
        && !isTencentMeeting(userAgent));
});
/**
 * 是否是 Chrome 浏览器
 */
export var isChrome = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(Chrome|CriOS)\/\S+( Mobile\S*)? Safari\/\S+$/i.test(userAgent);
});
/**
 * 企微 CEF 区别于标准 Chrome, UA 没有以 Safari 结尾
 */
var isWxWorkWindowsCEF = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /Chrome\/\S+ Safari\/\S+ /i.test(userAgent) && isWxWork(userAgent);
});
/**
 * 获取 Chrome 版本
 * 注意不代表一定是 Chrome H5，也可能是 Chrome 内核的 Android 客户端 H5、浏览器、小程序等，如果没有 Chrome 内核则为 null
 */
export var getChromeVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return getVersionFromUA('Chrome', userAgent);
});
/**
 * 判断 Chrome 是否高于指定的版本
 * 支持 Chrome 内核的 Android 客户端 H5、浏览器、小程序等，如果没有 Chrome 内核则为 false
 */
export var isChromeVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getChromeVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 Safari 浏览器
 */
export var isSafari = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /Version\/\S+( Mobile\S*)? Safari\/\S+$/i.test(userAgent);
});
/**
 * 获取 safari 版本
 */
export var getSafariVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isSafari(userAgent) ? getVersionFromUA('Version', userAgent) : null;
});
/**
 * 判断 safari 是否高于指定的版本
 */
export var isSafariVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getSafariVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 IE 浏览器
 */
export var isIE = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return (/MSIE/i.test(userAgent) && !/opera/i.test(userAgent)) || /Trident/i.test(userAgent);
});
/**
 * 是否是旧版 Edge 浏览器
 */
export var isOldEdge = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /Edge/i.test(userAgent);
});
/**
 * 是否是新版 Chromium Edge 浏览器
 */
export var isChromiumEdge = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /Edg\//i.test(userAgent);
});
/**
 * 是否是 Firefox 浏览器
 */
export var isFirefox = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(Firefox|Focus|FxiOS)\//i.test(userAgent);
});
/**
 * 获取 Firefox 版本
 */
export var getFirefoxVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isFirefox(userAgent) ? getVersionFromUA('(Firefox|Focus|FxiOS)', userAgent) : null;
});
/**
 * 判断 Firefox 是否高于指定的版本
 */
export var isFirefoxVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getFirefoxVersion(userAgent) || '', version) >= 0;
});
/** *******************************************************************************
 * 四、微信/企业微信 H5 判断
 *********************************************************************************/
/**
 * 是否是微信 H5
 * 自动排除企业微信和微信小程序
 */
export var isWeChat = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /MicroMessenger/i.test(userAgent) && !isWxWork(userAgent) && !isWxMiniProgram(userAgent);
});
/**
 * 获取微信版本
 * 同时支持微信 H5 和小程序 webview
 */
export var getWeChatVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isWeChat(userAgent) || isWxMiniProgram(userAgent) ? getVersionFromUA('MicroMessenger', userAgent) : null;
});
/**
 * 判断微信是否高于指定的版本
 * 同时支持微信 H5 和小程序 webview
 */
export var isWeChatVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getWeChatVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是企业微信
 * 注意：包含企业微信小程序
 */
export var isWxWork = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(wxwork)/i.test(userAgent);
});
/**
 * 是否是QT版企业微信
 */
export var isWxWorkQt = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(wxworkQt)/i.test(userAgent);
});
/**
 * @deprecated 企微 macOS 用了这个 ua，但是 Windows 没有用。请使用 isWxWorkCEF 代替
 * 判断是否是企业微信的定制客户端
 */
export var isWxWorkCustom = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /WeComCEF/i.test(userAgent) && isWxWork(userAgent);
});
/**
 * 是否是企业微信定制 CEF 浏览器
 */
export var isWxWorkCEF = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    if (isWxWork(userAgent) && isPC(userAgent)) {
        if (isMac(userAgent)) {
            return isWxWorkCustom(userAgent);
        }
        else {
            return isWxWorkWindowsCEF(userAgent);
        }
    }
    return false;
});
/**
 * 获取企业微信定制 CEF 版本
 */
export var getWxWorkCEFVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    if (isWxWork(userAgent) && isPC(userAgent)) {
        if (isMac(userAgent)) {
            return getVersionFromUA('WeComCEF', userAgent);
        }
        else {
            return getChromeVersion(userAgent);
        }
    }
    return null;
});
/**
 * 判断企业微信定制 CEF 是否高于指定的版本
 */
export var isWxWorkCEFVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getWxWorkCEFVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是私有化企业微信
 */
export var isWxWorkPrivate = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /(wxworklocal)/i.test(userAgent);
});
/**
 * 判断是否是微盘
 */
export var isWxDrive = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /WXDrive/i.test(userAgent);
});
/**
 * 获取微盘版本
 */
export var getWxDriveVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isWxDrive(userAgent) ? getVersionFromUA('WXDrive', userAgent) : null;
});
/**
 * 判断微盘版本是否高于指定的版本
 */
export var isWxDriveVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getWxDriveVersion(userAgent) || '', version) >= 0;
});
/**
 * 获取企业微信版本
 */
export var getWxWorkVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isWxWork(userAgent) ? getVersionFromUA('wxwork', userAgent) : null;
});
/**
 * 判断企业微信是否高于指定的版本
 */
export var isWxWorkVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getWxWorkVersion(userAgent) || '', version) >= 0;
});
/**
 * 获取私有化企业微信版本
 */
export var getWxWorkPrivateVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isWxWorkPrivate(userAgent) ? getVersionFromUA('wxworklocal', userAgent) : null;
});
/**
 * 判断私有化企业微信是否高于指定的版本
 */
export var isWxWorkPrivateVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getWxWorkPrivateVersion(userAgent) || '', version) >= 0;
});
/** *******************************************************************************
 * 五、QQ/TIM H5 判断
 *********************************************************************************/
/**
 * 是否是 QQ H5
 * 会自动排除文档独立 App / 企业微信 / 微信 / QQ 浏览器 / TIM
 */
export var isQQ = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /QQ\//i.test(userAgent) && !isTencentDocsApp(userAgent) && !isQQMiniProgram(userAgent) && !isTim(userAgent);
});
/**
 * 是否是 Google Play QQ
 * 注意：包含 QQ 小程序
 */
export var isQQForGooglePlay = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /QQ\//i.test(userAgent) && /_GM/i.test(userAgent);
});
/**
 * 获取 QQ 版本
 * 同时支持 QQ H5 和小程序 webview
 */
export var getQQVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isQQ(userAgent) || isQQMiniProgram(userAgent) ? getVersionFromUA('QQ', userAgent) : null;
});
/**
 * 判断 QQ 是否高于指定的版本
 * 同时支持 QQ H5 和小程序 webview
 */
export var isQQVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getQQVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 TIM
 * 注意：包含 TIM 小程序
 */
export var isTim = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    // 手机 TIM 4.0 新版本 UA 为 MIT
    return /(TIM|MIT)\//i.test(userAgent);
});
/**
 * 是否是 PC 端的 TIM
 */
export var isPCTim = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /QBCore/i.test(userAgent) && /TIM2.0/i.test(userAgent);
});
/**
 * 获取 TIM 版本
 */
export var getTimVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    // 手机 TIM 4.0 新版本 UA 为 MIT
    return isTim(userAgent) ? getVersionFromUA('TIM', userAgent) || getVersionFromUA('MIT', userAgent) : null;
});
/**
 * 判断 TIM 是否高于指定的版本
 */
export var isTimVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getTimVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 TIM 或者 QQ H5
 * 注意：不包含 QQ/TIM 小程序
 */
export var isTimOrQQ = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isTim(userAgent) && !isQQMiniProgram(userAgent) || isQQ(userAgent);
});
/**
 * 是否属于 TIM 或者泛 QQ 家族
 * 注意：原来的 isQQFamily 和这个含义一样
 */
export var isTimOrQQFamily = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isTim(userAgent) || /QQ/i.test(userAgent);
});
/**
 * 是否在 QQ mini 端（QQ 预览文档平台，可以理解为 QQ 桌面端内嵌的文档客户端）
 */
export var isQQDesktopDocsClient = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /QQEX/i.test(userAgent) && isElectronTencentDocsClient(userAgent);
});
/** *******************************************************************************
 * 六、小程序（webview/原生）判断
 *********************************************************************************/
/**
 * 是否是（微信/企业微信或 QQ/TIM）的小程序 webview
 */
export var isMiniProgram = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    // 企业微信 iOS 比较特殊，UA 中没有小程序的字样，只能通过全局 __wxjs_environment 来判断
    return (/miniprogram|miniapp(?!enable)/i.test(userAgent)
        // eslint-disable-next-line camelcase
        || (typeof __wxjs_environment !== 'undefined' && __wxjs_environment === 'miniprogram'));
});
/**
 * 是否是微信/企业微信小程序 webview
 */
export var isWxMiniProgram = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /MicroMessenger/i.test(userAgent) && isMiniProgram(userAgent);
});
/**
 * 是否是 QQ/TIM 小程序 webview
 */
export var isQQMiniProgram = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /QQ\/|TIM/i.test(userAgent) && isMiniProgram(userAgent);
});
/** *******************************************************************************
 * 七、独立 App 判断
 *********************************************************************************/
/**
 * 是否是移动客户端
 */
export var isTencentDocsApp = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /TencentDocs/i.test(userAgent);
});
/**
 * 获取腾讯文档移动客户端版本
 */
export var getTencentDocsAppVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return getVersionFromUA('TencentDocs', userAgent);
});
/**
 * 判断腾讯文档移动客户端是否高于指定的版本
 */
export var isTencentDocsAppVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getTencentDocsAppVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 PC 客户端
 * @deprecated 此为老的 Windows 客户端，现在已经不再使用，判断新客户端请改用 isElectronTencentDocsClient
 */
export var isPCTencentDocsClient = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /TxDocsPC/i.test(userAgent);
});
/**
 * 是否是 PC 客户端
 * @deprecated 此为老的 Windows 客户端，现在已经不再使用，判断新客户端请改用 isElectronTencentDocsClient
 */
export var isPCTencentDocsApp = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isPCTencentDocsClient(userAgent);
});
/**
 * 获取腾讯文档 PC 客户端版本
 * @deprecated 此为老的 Windows 客户端，现在已经不再使用，判断新客户端请改用 getElectronTencentDocsClientVersion
 */
export var getPCTencentDocsClientVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return getVersionFromUA('TxDocsPC', userAgent);
});
/**
 * 判断腾讯文档 PC 客户端是否高于指定的版本
 * @deprecated 此为老的 Windows 客户端，现在已经不再使用，判断新客户端请改用 isElectronTencentDocsClientVersionAboveOrEqual
 */
export var isPCTencentDocsClientVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getPCTencentDocsClientVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是 Electron 客户端
 */
export var isElectronTencentDocsClient = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /TDAppDesktop/i.test(userAgent);
});
/**
 * 获取腾讯文档 Electron 客户端版本
 */
export var getElectronTencentDocsClientVersion = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return getVersionFromUA('TDAppDesktop', userAgent);
});
/**
 * 判断腾讯文档 Electron 客户端是否高于指定的版本
 */
export var isElectronTencentDocsClientVersionAboveOrEqual = /* #__PURE__ */ withDefaultUAAndVersion(function (userAgent, version) {
    return compareVersion(getElectronTencentDocsClientVersion(userAgent) || '', version) >= 0;
});
/**
 * 是否是腾讯文档 PC 客户端 MacAppStore 版本
 */
export var isMacAppStoreTencentDocsClient = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return getVersionFromUA('TDAppDesktopChannel', userAgent) === '30020';
});
/**
 * 是否是微云 App
 */
export var isWeiYunApp = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /weiyun/i.test(userAgent);
});
/**
 * 是否是搜狗 windows 浏览器
 */
export var isSouGou = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /SE 2\.X MetaSr 1\.0/i.test(userAgent);
});
/**
 * 是否是腾讯会议
 */
export var isTencentMeeting = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /tencent_wemeet/i.test(userAgent);
});
/**
 * 是否是企微内腾讯会议
 */
export var isWxWorkTencentMeeting = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return isTencentMeeting(userAgent) && /meeting_wework/i.test(userAgent);
});
/**
 * 是否是腾讯会议的 Rooms 客户端（rooms 是腾讯会议的一个细分场景：嵌入式 app，常常嵌入在会议室的电子白板）
 */
export var isTencentMeetingRooms = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    return /TencentMeetingRooms/i.test(userAgent);
});
/**
 * 桌面端获取UA设置使用
 * @param option
 * @returns
 */
export var getBitToggleSettings = function (option) {
    return getBitToggleSetting(option);
};
/**
 *  桌面端获取UA设置使用
 */
export var isSupportNativeDatabase = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    var tdocFlag = userAgent.match(/_tdocFlag\/(\d+)/);
    if (tdocFlag === null || tdocFlag === void 0 ? void 0 : tdocFlag[1]) {
        var num = parseInt(tdocFlag[1], 10);
        return !!(num & TDocFlag.nativeDatabaseAndFullOfflineModeAvailable);
    }
    var bitToggle = userAgent.match(TOGGLE_KEY_PATTERN);
    if (bitToggle === null || bitToggle === void 0 ? void 0 : bitToggle[1]) {
        var num = parseInt(bitToggle[1], 10);
        return !!(num & TDBitToggle.nativeDatabaseAvailable);
    }
    return false;
});
/**
 * 是否支持全离线能力(离线编辑&离线打开)
 */
export var isSupportFullOfflineMode = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    var tdocFlag = userAgent.match(/_tdocFlag\/(\d+)/);
    if (tdocFlag === null || tdocFlag === void 0 ? void 0 : tdocFlag[1]) {
        var num = parseInt(tdocFlag[1], 10);
        return !!(num & TDocFlag.nativeDatabaseAndFullOfflineModeAvailable);
    }
    var bitToggle = userAgent.match(TOGGLE_KEY_PATTERN);
    if (bitToggle === null || bitToggle === void 0 ? void 0 : bitToggle[1]) {
        var num = parseInt(bitToggle[1], 10);
        return !!(num & TDBitToggle.fullOfflineModeAvailable);
    }
    return false;
});
/**
 * 客户端数据库是否即将废弃
 */
export var isNativeDatabaseWillDeprecated = /* #__PURE__ */ withDefaultUA(function (userAgent) {
    var bitToggle = userAgent.match(TOGGLE_KEY_PATTERN);
    if (bitToggle === null || bitToggle === void 0 ? void 0 : bitToggle[1]) {
        var num = parseInt(bitToggle[1], 10);
        return !!(num & TDBitToggle.nativeDatabaseWillDeprecated);
    }
    return false;
});
